import type {
  PermissionOption,
  PermissionRequest,
  ToolPermissionPresets,
} from "@/types";

const ALLOW_OPTION_KINDS = new Set([
  "allow_always",
  "allow_once",
  "approve",
  "allow",
]);

const REJECT_OPTION_KINDS = new Set([
  "reject_always",
  "reject_once",
  "deny",
]);

export function canonicalizePermissionToolName(toolName: string): string {
  return toolName.trim().toLowerCase();
}

export function normalizeToolPermissionPresets(input: unknown): ToolPermissionPresets {
  if (!input || typeof input !== "object") return {};

  const entries = Object.entries(input as Record<string, unknown>);
  const next: ToolPermissionPresets = {};

  for (const [rawKey, value] of entries) {
    if (!value || typeof value !== "object") continue;
    const preset = value as Record<string, unknown>;
    const behavior = preset.behavior;
    const toolName = typeof preset.toolName === "string" && preset.toolName.trim()
      ? preset.toolName.trim()
      : rawKey.trim();
    if (behavior !== "allow" || !toolName) continue;

    const key = canonicalizePermissionToolName(toolName);
    next[key] = {
      toolName,
      behavior: "allow",
      preferredOptionKind: typeof preset.preferredOptionKind === "string"
        ? preset.preferredOptionKind
        : null,
    };
  }

  return next;
}

export function isRejectPermissionOption(option: PermissionOption): boolean {
  return REJECT_OPTION_KINDS.has(option.kind);
}

export function isAllowPermissionOption(option: PermissionOption): boolean {
  if (ALLOW_OPTION_KINDS.has(option.kind)) return true;
  return !isRejectPermissionOption(option);
}

export function pickAutoApproveOption(
  options: PermissionOption[],
  preferredOptionKind?: string | null,
): PermissionOption | null {
  if (!Array.isArray(options) || options.length === 0) return null;

  if (preferredOptionKind) {
    const preferred = options.find((option) =>
      option.kind === preferredOptionKind && isAllowPermissionOption(option),
    );
    if (preferred) return preferred;
  }

  return (
    options.find((option) => option.kind === "allow_always")
    || options.find((option) => option.kind === "allow_once")
    || options.find((option) => option.kind === "approve")
    || options.find((option) => option.kind === "allow")
    || options.find((option) => isAllowPermissionOption(option))
    || null
  );
}

export function findAutoApproveOptionForRequest(
  request: PermissionRequest,
  presets: ToolPermissionPresets,
): PermissionOption | null {
  const preset = presets[canonicalizePermissionToolName(request.toolName)];
  if (!preset || preset.behavior !== "allow") return null;
  return pickAutoApproveOption(request.options, preset.preferredOptionKind);
}

export function upsertAllowPreset(
  presets: ToolPermissionPresets,
  request: PermissionRequest,
  selectedOptionId?: string | null,
): ToolPermissionPresets {
  const selectedOption = selectedOptionId
    ? request.options.find((option) => option.optionId === selectedOptionId)
    : null;
  const preferredOption = (
    selectedOption && isAllowPermissionOption(selectedOption)
      ? selectedOption
      : pickAutoApproveOption(request.options)
  );
  if (!preferredOption) return presets;

  const key = canonicalizePermissionToolName(request.toolName);
  return {
    ...presets,
    [key]: {
      toolName: request.toolName.trim() || request.toolName,
      behavior: "allow",
      preferredOptionKind: preferredOption.kind,
    },
  };
}

export function removePermissionPreset(
  presets: ToolPermissionPresets,
  toolName: string,
): ToolPermissionPresets {
  const key = canonicalizePermissionToolName(toolName);
  if (!presets[key]) return presets;

  const next = { ...presets };
  delete next[key];
  return next;
}
